import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Clock, Share2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';
import { getBlogPostBySlug, getBlogPosts, blogViewStart, blogViewBeat, blogShare } from '../services/api';
import { shouldSkipBlogTracking } from '../utils/blogTracking';
import { renderArticleContent } from '../utils/articleRenderer.jsx';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { Button } from '../components/ui/button';

const SITE_NAME = 'Orosz Coaching';
const AUTHOR_NAME = 'Orosz Gábor';

// Összevont cikkek: a régi URL-ről a megtartott cikkre küldjük az olvasót.
const MERGED_SLUGS = {
  'miert-nem-fogysz-deficitben': 'miert-allt-meg-a-fogyas',
};

const stripMd = (text = '') =>
  text.replace(/[#*`>]/g, '').replace(/\s+/g, ' ').trim();

const ANON_SESSION_KEY = 'oc_anon_session_v1';
// Persistent anon session id so returning readers can be de-duped in
// analytics ("unique visitors"). No PII, just a random UUID.
const getAnonSession = () => {
  if (typeof window === 'undefined') return '';
  let s = window.localStorage.getItem(ANON_SESSION_KEY);
  if (!s) {
    s = (crypto?.randomUUID?.() || String(Date.now()) + Math.random().toString(36).slice(2)).replace(/-/g, '').slice(0, 32);
    try { window.localStorage.setItem(ANON_SESSION_KEY, s); } catch { /* private mode */ }
  }
  return s;
};

/**
 * View tracker — bootstraps a view record when the post loads, then sends a
 * 20s heartbeat while the tab is visible. On unmount / route change /
 * page hide the interval stops naturally — final `last_seen` is whatever
 * the last heartbeat wrote, giving a rough time-on-page metric.
 */
const useBlogViewTracker = (slug) => {
  useEffect(() => {
    if (!slug) return;
    // Admin felületről megnyitott cikk nem számít bele a statisztikába.
    if (shouldSkipBlogTracking()) return;
    const session = getAnonSession();
    if (!session) return;
    let viewId = null;
    let intervalId = null;
    let cancelled = false;

    blogViewStart({ slug, session_id: session, referrer: document.referrer || null })
      .then((r) => {
        if (cancelled) return;
        viewId = r?.view_id;
        if (!viewId) return;
        intervalId = window.setInterval(() => {
          if (document.hidden) return;      // stop the clock while the tab is backgrounded
          blogViewBeat(viewId, { session_id: session }).catch(() => { /* silent */ });
        }, 20000);
      })
      .catch(() => { /* analytics is best-effort — never surface errors */ });

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
      if (viewId) blogViewBeat(viewId, { session_id: session }).catch(() => {});
    };
  }, [slug]);
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error
  const [bookingOpen, setBookingOpen] = useState(false);
  const mergedTarget = MERGED_SLUGS[slug];
  useBlogViewTracker(mergedTarget ? null : slug);

  // Összevont cikk régi URL-je: azonnal a megtartott cikkre visszük.
  useEffect(() => {
    if (mergedTarget) navigate(`/tudastar/${mergedTarget}`, { replace: true });
  }, [mergedTarget, navigate]);

  useEffect(() => {
    if (mergedTarget) return undefined;
    let alive = true;
    setStatus('loading');
    setPost(null);
    setRelated([]);

    // Scroll to top whenever the slug changes — fixes mobile "land at the
    // bottom of the article" issue when navigating from the home page.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }

    (async () => {
      try {
        const { post: fetched } = await getBlogPostBySlug(slug);
        if (!alive) return;
        setPost(fetched);
        setStatus('ok');
        // fetch the rest for "More articles" section
        try {
          const { posts } = await getBlogPosts();
          if (!alive) return;
          const others = (posts || []).filter((p) => p.slug !== fetched.slug);
          const sameCat = others.filter((p) => p.category === fetched.category);
          const rest = others.filter((p) => p.category !== fetched.category);
          setRelated([...sameCat, ...rest].slice(0, 3));
        } catch (_) {
          /* non-fatal */
        }
      } catch (err) {
        if (!alive) return;
        if (axios.isAxiosError?.(err) && err.response?.status === 404) {
          setStatus('notfound');
        } else {
          setStatus('error');
        }
      }
    })();

    return () => { alive = false; };
  }, [slug]);

  // SEO meta — only when post is loaded
  const canonical = post ? `${window.location.origin}/tudastar/${post.slug}` : undefined;
  const jsonLd = post ? {
    '@context': 'https://schema.org',
    '@type':    'BlogPosting',
    headline:        post.title,
    description:     post.excerpt,
    image:           post.image,
    datePublished:   post.date,
    dateModified:    post.updated_at || post.date,
    inLanguage:      'hu-HU',
    articleSection:  post.category,
    author:    { '@type': 'Person',       name: AUTHOR_NAME },
    publisher: {
      '@type': 'Organization',
      name:    SITE_NAME,
      logo: { '@type': 'ImageObject', url: `${window.location.origin}/favicon.ico` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
  } : null;

  useDocumentMeta(post ? {
    title:       `${post.title} | ${SITE_NAME}`,
    description: stripMd(post.excerpt).slice(0, 160),
    canonical,
    image:       post.image,
    type:        'article',
    jsonLd,
  } : { title: status === 'notfound' ? `Cikk nem található | ${SITE_NAME}` : `Tudástár | ${SITE_NAME}` });

  const handleShare = async () => {
    const url = canonical || window.location.href;
    const trackShare = (channel) => {
      const session = getAnonSession();
      if (!session || !post?.slug) return;
      blogShare({ slug: post.slug, session_id: session, channel }).catch(() => {});
    };
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, text: post.excerpt, url });
        trackShare('native');
        return;
      } catch (_) { /* user cancelled or unsupported */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      trackShare('clipboard');
      toast.success('Link kimásolva a vágólapra!');
    } catch (_) {
      toast.error('A megosztás nem sikerült – másold ki manuálisan az URL-t.');
    }
  };

  // ----- Render states ----------------------------------------------------------
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="container mx-auto px-6 py-32 text-center">
          <p className="text-gray-600" data-testid="blog-post-loading">Cikk betöltése…</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (status === 'notfound') {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="container mx-auto px-6 py-32 text-center flex-1">
          <div className="inline-block mb-4 px-4 py-2 bg-yellow-100 rounded-full">
            <span className="text-yellow-700 font-semibold text-sm tracking-wider">404</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ezt a cikket nem találjuk
          </h1>
          <p className="text-gray-600 mb-8 max-w-lg mx-auto">
            Lehet, hogy a link elavult, vagy a cikk átmenetileg nem elérhető.
            Térj vissza a Tudástárba, és böngéssz a legfrissebb írások között.
          </p>
          <Button
            data-testid="blog-post-back-to-home"
            onClick={() => navigate('/#blog')}
            className="bg-gray-900 hover:bg-yellow-600 text-white font-semibold"
          >
            <ArrowLeft className="mr-2" size={16} />
            Vissza a Tudástárba
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  if (status === 'error' || !post) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="container mx-auto px-6 py-32 text-center flex-1">
          <p className="text-red-600">Hiba történt a cikk betöltése közben. Próbáld újra később.</p>
        </main>
        <Footer />
      </div>
    );
  }

  // ----- Article view -----------------------------------------------------------
  return (
    <div className="min-h-screen bg-white">
      <Header />

      <article data-testid="blog-post-page" className="pt-24 pb-20">
        {/* Hero image */}
        <div className="relative h-72 md:h-[420px] overflow-hidden">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/85 via-gray-900/40 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="container mx-auto px-6 pb-10 md:pb-14">
              <div className="max-w-3xl text-white">
                <Link
                  to="/#blog"
                  data-testid="blog-post-breadcrumb"
                  className="inline-flex items-center text-sm text-yellow-300 hover:text-yellow-200 mb-4 transition-colors"
                >
                  <ArrowLeft className="mr-1.5" size={14} />
                  Tudástár
                  <ChevronRight className="mx-1 opacity-60" size={14} />
                  <span className="text-white/80">{post.category}</span>
                </Link>
                <h1
                  data-testid="blog-post-title"
                  className="text-3xl md:text-5xl font-bold leading-tight mb-4"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {post.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-white/85">
                  <span className="inline-block bg-yellow-600 text-white text-xs font-bold px-3 py-1 rounded-full tracking-wider">
                    {post.category}
                  </span>
                  <span className="flex items-center gap-1.5"><Calendar size={14} />
                    {new Date(post.date).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1.5"><Clock size={14} />{post.read_time}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="container mx-auto px-6 mt-12">
          <div className="max-w-3xl mx-auto">
            <p
              data-testid="blog-post-excerpt"
              className="text-lg md:text-xl text-gray-600 italic border-l-4 border-yellow-600 pl-5 mb-10"
            >
              {post.excerpt}
            </p>

            <div data-testid="blog-post-body" className="prose-like">
              {renderArticleContent(post.content, { onBooking: () => setBookingOpen(true) })}
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                <span>Írta:</span>
                <strong className="text-gray-900">{AUTHOR_NAME}</strong>
                <span className="text-gray-300">·</span>
                <span data-testid="blog-post-published-date">
                  Megjelent:{' '}
                  {new Date(post.date).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
                {post.updated_at
                  && new Date(post.updated_at).toDateString() !== new Date(post.date).toDateString()
                  && new Date(post.updated_at) > new Date(post.date) && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span data-testid="blog-post-updated-date">
                      Frissítve:{' '}
                      {new Date(post.updated_at).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                  </>
                )}
              </div>
              <Button
                data-testid="blog-post-share-button"
                onClick={handleShare}
                variant="outline"
                className="border-gray-300 hover:bg-yellow-50 hover:border-yellow-600 hover:text-yellow-700 text-gray-700 font-semibold"
              >
                <Share2 className="mr-2" size={16} />
                Cikk megosztása
              </Button>
            </div>

            {/* Belső linkek — segítenek a látogatónak és a Google-nek is */}
            <div
              data-testid="blog-post-internal-links"
              className="mt-10 rounded-2xl border border-yellow-200 bg-yellow-50/60 p-5 md:p-6 space-y-3"
            >
              <Link
                to="/szemelyi-edzo-miskolc"
                data-testid="blog-link-miskolc"
                className="group flex items-start gap-2 text-sm md:text-base font-semibold text-gray-900 hover:text-yellow-700 transition-colors"
              >
                <span>Személyi edzőt keresel Miskolcon? Nézd meg, hogyan dolgozom</span>
                <ChevronRight className="shrink-0 mt-0.5 group-hover:translate-x-1 transition-transform" size={18} />
              </Link>
              <Link
                to="/online-szemelyi-edzo"
                data-testid="blog-link-online"
                className="group flex items-start gap-2 text-sm md:text-base font-semibold text-gray-900 hover:text-yellow-700 transition-colors"
              >
                <span>Nem Miskolcon élsz? Online is segítek</span>
                <ChevronRight className="shrink-0 mt-0.5 group-hover:translate-x-1 transition-transform" size={18} />
              </Link>
            </div>

            {/* CTA */}
            <div className="mt-12 bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-2xl p-8 md:p-10">
              <h3 className="text-xl md:text-2xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Készen állsz a valódi átalakulásra?
              </h3>
              <p className="text-white/80 mb-5 leading-relaxed">
                Foglalj egy ingyenes konzultációs időpontot, és nézzük át együtt, melyik programom illik a leginkább a céljaidhoz és az időbeosztásodhoz.
              </p>
              <Button
                data-testid="blog-post-cta-book"
                onClick={() => setBookingOpen(true)}
                className="bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-bold"
              >
                Időpontfoglalás
                <ChevronRight className="ml-1" size={18} />
              </Button>
            </div>
          </div>
        </div>

        {/* More articles */}
        {related.length > 0 && (
          <div className="container mx-auto px-6 mt-20">
            <div className="max-w-5xl mx-auto">
              <h4
                className="text-xl md:text-2xl font-bold text-gray-900 mb-6"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                További cikkek a Tudástárból
              </h4>
              <div className="grid md:grid-cols-3 gap-6">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/tudastar/${r.slug}`}
                    data-testid={`blog-post-related-${r.id}`}
                    className="group block bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-yellow-300 transition-all"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <span className="absolute top-3 left-3 bg-yellow-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">{r.category}</span>
                    </div>
                    <div className="p-5">
                      <h5 className="font-bold text-gray-900 group-hover:text-yellow-600 leading-snug mb-2">
                        {r.title}
                      </h5>
                      <p className="text-sm text-gray-600 line-clamp-2">{r.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </article>

      <Footer />
      <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
};

export default BlogPostPage;
